import { useMemo, useState } from "react";
import Shimmer from "../components/Shimmer.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useRecentTransactions } from "../hooks/useRecentTransactions.js";
import { formatUsdcCompact, shortAddress } from "../utils/format.js";

const TOKEN_SYMBOL = "USDC";

/**
 * Per-type display metadata for the activity feed. `tone` is only a
 * fallback — the indexed Transaction row carries its own `direction`,
 * which the renderer trusts first.
 */
const TYPE_META = {
    deposit_opened: { label: "Deposit Opened", tone: "out" },
    deposit_settled: { label: "Deposit Settled", tone: "in" },
    interest_withdrawn: { label: "Interest Withdrawn", tone: "in" },
    early_exit: { label: "Early Exit", tone: "in" },
    abandoned_fee: { label: "Abandoned Fee", tone: "out" },
    loan_opened: { label: "Loan Drawn", tone: "in" },
    loan_repaid: { label: "Loan Repaid", tone: "out" },
    loan_liquidated: { label: "Loan Liquidated", tone: "out" },
    nft_transfer_in: { label: "NFT Received", tone: "in" },
    nft_transfer_out: { label: "NFT Sent", tone: "out" }
};

const FILTERS = [
    { id: "all", label: "All" },
    { id: "deposit_opened", label: "Deposits" },
    { id: "deposit_settled", label: "Settlements" },
    { id: "early_exit", label: "Early Exits" },
    // The Loans tab groups every loan event — draws, repayments and
    // liquidations — so split repayments all surface in one place.
    { id: "loans", label: "Loans" },
    { id: "nft_transfer_out", label: "NFT Transfers" }
];

/**
 * Activity — a unified, filterable feed of the connected wallet's
 * on-chain history (deposits, settlements, early exits, loans, NFT
 * transfers, decay fees). Sourced entirely from the backend indexer's
 * Transaction projection; the frontend never reads history from chain.
 */
export default function Activity() {
    const { address } = useWallet();
    const { items, isLoading, error, refresh } = useRecentTransactions(address, { limit: 200, pollMs: 0 });
    const [filter, setFilter] = useState("all");

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        if (filter === "loans") {
            // Every loan event: draws, repayments (incl. split payments)
            // and liquidations.
            return items.filter((t) => String(t.type).startsWith("loan_"));
        }
        if (filter === "nft_transfer_out") {
            return items.filter((t) => t.type === "nft_transfer_out" || t.type === "nft_transfer_in");
        }
        return items.filter((t) => t.type === filter);
    }, [items, filter]);

    const totals = useMemo(() => {
        let inflow = 0n;
        let outflow = 0n;
        for (const t of items) {
            const amount = BigInt(t.amount || 0);
            if (t.direction === "in") inflow += amount;
            else outflow += amount;
        }
        return { inflow, outflow };
    }, [items]);

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Connect your wallet to see your activity feed.
                    </p>
                </div>
            </div>
        );
    }

    const stats = [
        {
            label: "Total Activity",
            value: String(items.length),
            unit: "events",
            delta: isLoading ? "Loading" : "Indexed",
            iconSrc: STAT_ICONS.active
        },
        {
            label: "Total Received",
            value: `$${formatUsdcCompact(totals.inflow)}`,
            unit: TOKEN_SYMBOL,
            delta: "Inflows",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Total Sent",
            value: `$${formatUsdcCompact(totals.outflow)}`,
            unit: TOKEN_SYMBOL,
            delta: "Outflows",
            iconSrc: STAT_ICONS.safe
        }
    ];

    return (
        <div className="flex flex-col gap-3">

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {stats.map((item) => (
                    <StatCard key={item.label} {...item} loading={isLoading} />
                ))}
            </div>

            <div className="flex justify-between">
                <div  className="flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            onClick={() => setFilter(f.id)}
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${
                                filter === f.id
                                    ? "bg-[#FBC206] text-[#10281b]"
                                    : "bg-[#10281b] text-[#e6c985] ring-1 ring-[#bfb67b] hover:bg-[#16331f]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                        error
                            ? "bg-[#FBC206] text-[#10281b] ring-1 ring-[#FBC206] hover:bg-[#e6af05]"
                            : "bg-[#10281b] text-[#e6c985] ring-1 ring-[#bfb67b] hover:bg-[#16331f]"
                    }`}
                >
                    <RefreshIcon />
                    Refresh
                </button>
            </div>

            <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                    Transaction History
                </h3>

                <ul className="mt-3 space-y-2">
                    {isLoading && items.length === 0
                        ? Array.from({ length: 5 }).map((_, i) => (
                              <li key={`skel-${i}`}>
                                  <Shimmer width="100%" height="3rem" />
                              </li>
                          ))
                        : null}

                    {!isLoading && error ? (
                        <li className="rounded-[10px] bg-[#FBC206]/10 ring-1 ring-[#FBC206]/60 px-3 py-6 text-center text-[12px] text-[#FBC206]">
                            <div className="font-manrope font-bold">Couldn&apos;t load activity. Tap Refresh to retry.</div>
                            <div className="mt-1 text-[10px] text-[#FBC206]/70">{String(error)}</div>
                        </li>
                    ) : null}

                    {!isLoading && !error && filtered.length === 0 ? (
                        <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-6 text-center text-[12px] text-[#e6c985]/70">
                            No activity yet for this filter.
                        </li>
                    ) : null}

                    {filtered.map((tx) => (
                        <ActivityRow key={`${tx.txHash}-${tx.logIndex}`} tx={tx} />
                    ))}
                </ul>
            </section>
        </div>
    );
}

function ActivityRow({ tx }) {
    const meta = TYPE_META[tx.type] || { label: tx.type, tone: tx.direction === "in" ? "in" : "out" };
    const isInflow = tx.direction === "in";
    const explorer = config.network.blockExplorerUrl;
    const when = tx.timestamp ? new Date(tx.timestamp) : null;

    return (
        <li className="flex items-center justify-between rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-2.5">
            <div className="flex items-center gap-3">
                <span
                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        isInflow ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#FBC206]/15 text-[#FBC206]"
                    }`}
                >
                    {isInflow ? <ArrowDownIcon /> : <ArrowUpIcon />}
                </span>
                <div>
                    <div className="font-manrope text-[12.5px] font-bold text-white">
                        {meta.label}
                        {tx.depositId != null ? (
                            <span className="ml-1.5 text-[#e6c985]/70">· TD #{tx.depositId}</span>
                        ) : null}
                    </div>
                    <div className="mt-0.5 text-[10px] text-[#e6c985]/60">
                        {when ? when.toLocaleString() : "—"}
                        {explorer && tx.txHash ? (
                            <>
                                {"  ·  "}
                                <a
                                    href={`${explorer}/tx/${tx.txHash}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[#FBC206] hover:underline"
                                >
                                    {shortAddress(tx.txHash)}
                                </a>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="text-right">
                <div
                    className={`font-manrope text-[13px] font-bold ${
                        isInflow ? "text-[#22C55E]" : "text-[#FBC206]"
                    }`}
                >
                    {isInflow ? "+" : "−"}${formatUsdcCompact(tx.amount)}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#e6c985]/55">
                    {TOKEN_SYMBOL}
                </div>
            </div>
        </li>
    );
}

function PulseIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 12h4l3 8 4-16 3 8h4" />
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="21 4 21 10 15 10" />
            <path d="M19.4 9A8 8 0 1 0 21 14" />
        </svg>
    );
}

function ArrowDownIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="10" y1="4" x2="10" y2="15" />
            <polyline points="5 10 10 15 15 10" />
        </svg>
    );
}

function ArrowUpIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="10" y1="16" x2="10" y2="5" />
            <polyline points="5 10 10 5 15 10" />
        </svg>
    );
}
